import React, { useState, useEffect } from 'react';
import { Card, Table, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { adminApi } from '../../api';
import { formatDateAndTime, uploadDocument } from '../../utils';

import TooltipButton from '../common/TooltipButton';
import Loader from '../common/Loader';
import DocumentUploadModal from '../common/DocumentUploadModal';
import DocumentPreviewModal from '../common/DocumentPreviewModal';
import CopyButton from '../common/CopyButton';

import { useAlerts } from '../context';

function EndUserDocuments({ initialValues }) {
  const [loaded, setLoaded] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [upload, setUpload] = useState({ show: false, percentage: {} });
  const [preview, setPreview] = useState({ show: false, data: undefined });
  const [types, setTypes] = useState([]);
  const { t } = useTranslation();
  const { setAlert } = useAlerts();

  const getDocumentTypes = async () => {
    try {
      const response = await adminApi.getDocumentTypes();
      setTypes(response.document_types);
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
    }
  };

  const getEndUserDocuments = async () => {
    try {
      const response = await adminApi.getEndUserDocuments(initialValues.handle);
      setDocuments(response.documents);
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
    } finally {
      setLoaded(true);
    }
  };

  const getEndUserDocument = async (document) => {
    if (document) {
      setPreview({ show: true, data: undefined });
      try {
        const response = await adminApi.getEndUserDocument(initialValues.handle, document.uuid);
        const blob = new Blob([response], {
          type: document.file_type === 'pdf' ? 'application/pdf' : `image/${document.file_type}`,
        });
        setPreview({ show: true, data: { ...document, file: URL.createObjectURL(blob) } });
      } catch (error) {
        setAlert({ message: error, variant: 'warning' });
        setPreview({ show: false, data: undefined });
      }
    }
  };

  const addDocument = (values, setSubmitting) => {
    if (values) {
      const { name } = values.files[0];
      const data = {
        name,
        filename: name,
        mime_type: values.files[0].type,
        document_type: values.document_type,
        identity_type: values.identity_type,
        provider: values.provider,
        verification_uuid: values.verification_uuid,
      };

      uploadDocument({
        url: `/superuser/end_users/${initialValues.handle}/documents`,
        file: values.files[0],
        data: data,
        onUpload: (percentage) =>
          setUpload({ ...upload, percentage: { ...upload.percentage, [name]: percentage } }),
        onError: (status, error) => {
          if (setSubmitting) setSubmitting(false);
          setAlert({
            message: status === 413 ? t('common:upload.form.messages.filesize') : error,
            style: 'danger',
          });
          setUpload({ ...upload, percentage: {} });
        },
        onSuccess: (message) => {
          delete upload.percentage[name];
          setUpload({ show: false, percentage: upload.percentage });
          setAlert({ message, style: 'success' });
          getEndUserDocuments();
        },
      });
    } else {
      setUpload({ show: false, percentage: {} });
    }
  };

  useEffect(() => {
    getDocumentTypes();
    getEndUserDocuments();
  }, []);

  return (
    <>
      <h4 className="mb-2">{t('admin.endUsers.docs.title')}</h4>
      <p className="text-info mb-4">{t('admin.endUsers.docs.description')}</p>

      {!loaded || types.length === 0 ? (
        <Loader />
      ) : (
        <>
          <Card className="mb-2 border overflow-hidden">
            <Table responsive>
              <thead>
                <tr>
                  <th className="border-end">{t('common:upload.labels.date')}</th>
                  <th className="border-end">{t('common:upload.labels.name')}</th>
                  <th className="border-end">{t('common:upload.labels.type')}</th>
                  <th>{t('common:labels.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {documents.length !== 0 ? (
                  documents.map((document) => (
                    <tr key={document.uuid}>
                      <td className="border-end">{formatDateAndTime(document.uploaded)}</td>
                      <td className="border-end">
                        <span
                          className="w-100 d-block text-break pe-3 text-start"
                          style={{ maxWidth: '300px' }}
                        >
                          {document.file_name}
                        </span>
                      </td>
                      <td className="border-end">
                        {types.find((type) => type.name === document.document_type).label}
                      </td>
                      <td>
                        <div className="d-flex justify-content-around">
                          <TooltipButton
                            placement="top"
                            variant="link"
                            popoverProps={{ style: { textAlign: 'center' } }}
                            onClick={() => getEndUserDocument(document)}
                            text={t('common:labels.preview')}
                            className="p-0 cursor-pointer"
                          >
                            <i className="text-lg fas fa-eye" />
                          </TooltipButton>
                          <CopyButton
                            placement="top"
                            variant="link"
                            popoverProps={{ style: { textAlign: 'center' } }}
                            className="p-0 cursor-pointer"
                            handleClick={() => navigator.clipboard.writeText(document.uuid)}
                            labelText={t('admin.endUsers.docs.copy')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="disabled">
                    <td colSpan="4">
                      <p className="text-center text-info font-italic mx-4 my-5 cursor-default">
                        {t('admin.endUsers.docs.empty')}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card>

          <p className="loaded text-end mb-5">
            <Button
              variant="link"
              className="p-0 text-underline"
              onClick={() => setUpload({ ...upload, show: true })}
            >
              {t('common:upload.buttons.upload')}
            </Button>
          </p>
        </>
      )}

      {upload.show && (
        <DocumentUploadModal
          show={upload.show}
          types={types}
          percentage={upload.percentage}
          onHide={addDocument}
          verifications={initialValues.verification_history}
        />
      )}

      {preview.show && (
        <DocumentPreviewModal
          show={preview.show}
          data={preview.data}
          onHide={() => setPreview({ show: false, data: undefined })}
        />
      )}
    </>
  );
}

EndUserDocuments.propTypes = {
  /**
   * The values of the form
   */
  // eslint-disable-next-line react/forbid-prop-types
  initialValues: PropTypes.object.isRequired,
};

export default EndUserDocuments;
